import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import ResponseError from "#/types/responseError"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { PhoneSupportNumber } from "#/lib/carrot-api/types/PhoneNumber"
import { CountryCode } from "#/utils/CountryCode"
import useGetMemberCompany from "#/components/company/hooks/useGetMemberCompany"
import useGetMemberCountry from "#/components/company/hooks/useGetMemberCountry"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"
import { CARE_TEAM_PHONE_E164, CARE_TEAM_PHONE_INTERNATIONAL } from "@internal/constants/care-team/contacts"

const phoneSupportNumberQueryKey = "getPhoneSupportNumber"

/**
 * What a consumer member is answered instead of their company's number (CONAPP-472).
 *
 * The endpoint below is keyed on `companyId`, and a consumer's company record carries the
 * ENTERPRISE care-team number — so every surface that printed this hook's answer was handing a
 * consumer a line staffed for employer-sponsored members. Confirmed on seven of them, the
 * consumer-only Appointments page included.
 */
const consumerPhoneSupportNumber: PhoneSupportNumber = {
  e164FormatPhoneNumber: CARE_TEAM_PHONE_E164,
  internationalFormatPhoneNumber: CARE_TEAM_PHONE_INTERNATIONAL
}

function useGetPhoneSupportNumber(
  companyId: number,
  countryCode: CountryCode,
  isConsumer: boolean,
  programPending: boolean
): UseQueryResult<PhoneSupportNumber, ResponseError> {
  const query = useQuery<PhoneSupportNumber, ResponseError>(
    // ⚠ THE PROGRAM DISCRIMINATOR IS LOAD-BEARING, not decoration. Nothing here sets `staleTime`
    // and `refetchOnMount` is off, so an entry that lands once is read for the rest of the
    // session — and two members with the same `countryCode` can want DIFFERENT answers. Without
    // it, an enterprise entry cached under `["getPhoneSupportNumber", "US"]` is served verbatim
    // to a consumer, which is the exact bug this branch exists to fix.
    [phoneSupportNumberQueryKey, countryCode, isConsumer ? "consumer" : "enterprise"],
    isConsumer
      ? () => Promise.resolve(consumerPhoneSupportNumber)
      : () => carrotClient.getPhoneSupportNumber({ companyId, countryCode }),
    {
      // A consumer needs neither id: their answer is a constant, so the request is not merely
      // wasted but actively harmful — a failure would report to Sentry for a number nobody reads.
      enabled: isConsumer || (!programPending && !!companyId && !!countryCode),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: buildCarrotApiRetryPolicy(3),
      onError: (error) =>
        reportErrorMessage(
          `Failed to fetch phone number for company ${companyId} and countryCode ${countryCode}, error: ${error}`
        )
    }
  )

  // ⚠ THE PROGRAM READ'S OWN PENDING STATE HAS TO SURFACE AS THIS QUERY'S, because until it
  // settles we do not know WHICH number this member should see. The query above is disabled in
  // that window, and react-query v4 reports `isInitialLoading: false` for a disabled query —
  // which every display site reads as "the answer has arrived". `OtherWaysToContactCarrotTile`
  // would paint its no-number fallback (the "talk to us online" copy, whose button opens the
  // Twilio widget) for a beat and then snap to the number. Folding it in here keeps the single
  // check each call site already makes correct, instead of making seven of them ask twice.
  return programPending
    ? ({ ...query, data: undefined, isLoading: true, isInitialLoading: true } as UseQueryResult<
        PhoneSupportNumber,
        ResponseError
      >)
    : query
}

export function useGetPhoneSupportNumberForUser(): UseQueryResult<PhoneSupportNumber, ResponseError> {
  const memberUserCountry = useGetMemberCountry()
  const memberUserCompany = useGetMemberCompany()
  // Free to add: this is the same `member.program` query the navigation, the user dropdown and
  // the paywall gate already read, so react-query serves all of them from one fetch.
  const { data: programFlags, isLoading: programPending } = useMemberProgramFlags()

  return useGetPhoneSupportNumber(
    memberUserCompany?.data?.companyId,
    memberUserCountry?.data?.countryCode,
    programFlags?.isConsumer === true,
    programPending
  )
}
